import React from 'react'

import { Typography, useTheme } from '@mui/material'

type Props = {
  children: React.ReactNode
  /** Drop the leading gap when the label is the first thing in its container. */
  first?: boolean
  /**
   * Sitting on a shared row (e.g. beside the Email/SMS column captions in
   * `PreferenceChannelHeader`) rather than on its own line. Drops both margins
   * so the row's own alignment controls the spacing. Without this the label's
   * `mb` pushes it off the row's centre line.
   */
  inline?: boolean
  /**
   * Which register the caption is speaking in. Defaults to `'section'` — the
   * full-contrast page treatment every existing call site gets.
   *
   * `'menu'` is the QUIETER one, for a caption inside a small popover or sheet.
   * It exists because the argument for full contrast below is an argument about
   * PAGES, and it does not transfer:
   *
   * On Profile or a certificate page the caption competes with dozens of dark
   * rows spread over several screens, and it is the only thing marking where one
   * group ends and the next begins — so a grey caption is genuinely lost, which
   * is what `color` explains. A menu is the opposite situation. The Display
   * popover is 296px wide with two captions and six rows, all of it in view at
   * once: nothing has to be FOUND, so contrast buys no legibility, and at 600
   * the two captions were simply the heaviest ink in a panel whose actual
   * content is the rows beneath them.
   *
   * So the two are deliberately different, and neither should be "corrected" to
   * match the other. Uppercase + 0.06em is what makes both read as labels; the
   * register only says how loudly.
   */
  tone?: 'section' | 'menu'
}

/**
 * Small uppercase caption that names a group of rows.
 *
 * Values are taken verbatim from the Share page's module groups
 * (`pages/SharePage/components/ViewSettingsDrawer.tsx`) so the two surfaces read
 * as one system; extracted here on the second use (working-conventions §36)
 * rather than copying the sx block a third time. If the treatment changes,
 * change it here and migrate ViewSettingsDrawer onto this component.
 *
 * Use it to label a group of TWO OR MORE rows. A label over a single row is the
 * single-child-group anti-pattern (§20) and reads as chrome with no content.
 */
export const SectionLabel = ({
  children,
  first = false,
  inline = false,
  tone = 'section',
}: Props) => {
  const theme = useTheme()
  const menu = tone === 'menu'

  return (
    <Typography
      variant='body2'
      sx={{
        /*
         * 32 above, 8 below — a 4:1 ratio, and the ratio is the whole point.
         *
         * At 16/8 the caption sat almost equidistant between the section it ENDS
         * and the one it BEGINS, so DETAILS ran into HISTORY: with hairlines
         * between every row as well, the page read as one continuous ruled list
         * with words occasionally interrupting it. A caption has to belong
         * visibly to what FOLLOWS it, and proximity is what says so — air above,
         * tight below.
         *
         * (These are `apps/app`'s 8px base, so 4 = 32px and 1 = 8px. The same
         * factors mean half as much under @repo/ui's 4px base — see §29.)
         */
        /*
         * 32 between sections — back down from 48, which over-corrected.
         *
         * The first pass at 16 was genuinely too tight, but raising the gap was
         * only half of what fixed it; the caption also went from grey to full
         * contrast, and THAT is what turned it into a boundary. With a legible
         * caption, 48px of air is just a hole in the page — Profile showed it
         * clearest, where three short sections ended up further apart than the
         * avatar was from the first of them.
         */
        mt: inline ? 0 : first ? 0 : 4,
        mb: inline ? 0 : 1,
        /*
         * PRIMARY, not secondary. At grey the caption was the quietest thing on
         * a page of dark rows, so the one element marking a boundary was also the
         * one hardest to see — the eye ran straight past it into the next group.
         * Uppercase + tracking already keep it from competing with the row text;
         * they are what make it read as a label rather than as content, and they
         * do that job at full contrast too.
         */
        color: menu ? theme.palette.text.secondary : theme.palette.text.primary,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        /*
         * 600, one step ABOVE FieldLabel's 500 — a section heading must outrank
         * the field labels beneath it.
         *
         * This was 500, which was correct while FieldLabel was 16px/400: the
         * caption was smaller but heavier, so the two were distinguishable. When
         * FieldLabel moved to 14/500/primary (fixing an inversion where a field
         * label was LARGER than the section heading above it), the two landed on
         * the same size AND the same weight, separated only by case, tracking and
         * margin. Measured in the browser: both 14px/500/rgb(0,0,0).
         *
         * Uppercase + 0.06em + 32px of air is a real distinction and arguably
         * enough on its own — this is the classic eyebrow/overline pattern. But
         * "arguably enough" is not the same as ranking, and the two only ever look
         * tied where it matters most: a heading sitting directly above a field.
         * One weight step settles it without touching size.
         */
        /*
         * 500 in the menu register, one step DOWN from the section's 600.
         *
         * The step matters more than the number: the rows a menu caption labels
         * are 400, so 500 still outranks them, while 600 at grey read as a
         * second emphasis fighting the first. Weight and colour were both doing
         * "look at me" and the panel had no quiet part left.
         */
        fontWeight: menu ? 500 : 600,
      }}
    >
      {children}
    </Typography>
  )
}
